import React, { useEffect, useState } from "react";
import { MessageCircle, Send } from "lucide-react";
import api from "../lib/api";
import { apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * Generic comment thread.
 * scope: 'product' | 'deal'
 * scopeId: product_id or deal_id
 * onPosted: callback after a successful post
 */
export default function CommentThread({ scope, scopeId, onOpenAuth, onPosted, title = "Discussion", emptyText, testIdPrefix = "" }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const path = scope === "product" ? `/products/${scopeId}/comments` : `/deals/${scopeId}/comments`;

  const load = async () => {
    const res = await api.get(path);
    setComments(res.data.comments || []);
  };

  useEffect(() => { if (scopeId) load(); /* eslint-disable-next-line */ }, [scopeId, scope]);

  const post = async () => {
    if (!user) return onOpenAuth?.();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(path, { text: text.trim() });
      setText("");
      await load();
      onPosted?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to post"));
    } finally { setBusy(false); }
  };

  return (
    <section data-testid={`${testIdPrefix}comments-section`}>
      <h3 className="font-heading font-bold text-xl flex items-center gap-2">
        <MessageCircle size={18} /> {title} <span className="text-muted-foreground font-medium">({comments.length})</span>
      </h3>

      <div className="mt-4 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && post()}
          placeholder={user ? "Share your thoughts, quotes, questions…" : "Sign in to comment"}
          className="input-bhega"
          data-testid={`${testIdPrefix}comment-input`}
        />
        <button onClick={post} disabled={busy} className="btn-primary" data-testid={`${testIdPrefix}comment-submit`}>
          <Send size={14} /> Post
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {comments.length === 0 && (
          <p className="text-sm text-muted-foreground">{emptyText || "No comments yet. Be the first to discuss."}</p>
        )}
        {comments.map((c) => (
          <div key={c.id} className="border border-border p-3 rounded-none" data-testid={`${testIdPrefix}comment-${c.id}`}>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-[#FFBE06] text-black rounded-full text-xs font-bold flex items-center justify-center">
                {(c.user_name || "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="text-xs">
                <strong className="text-foreground">{c.user_name || "Buyer"}</strong>
                <span className="text-muted-foreground ml-2">{new Date(c.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
              </div>
            </div>
            <p className="text-sm mt-2 leading-relaxed">{c.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
